'use client'
import * as React from 'react'
import { createStore, useStore } from 'zustand'
import { useRepoStoreRoot } from './repo-store'

export type EditorState = {
  selectedBlockId: string | null
  hoveredBlockId: string | null
  isRenamingBlockId: string | null
  justCreatedTableRowId: string | null
  selectedBlockAwareness: string | null
  aiGeneration: {
    status: 'idle' | 'input-exiting' | 'blocks-entering' | 'done'
    generatedBlockIds: string[]
  } | null
  recentlyUploadedImages: Map<
    string,
    {
      width: number
      height: number
      src: string
    } | null
  >
  setRecentlyUploadedImageById: (
    blockId: string,
    image: {
      width: number
      height: number
      src: string
    } | null
  ) => void
  setSelectedBlockId: (blockId: string | null) => void
  setSelectedBlockAwareness: (awareness: string | null) => void
  setHoveredBlockId: (blockId: string | null) => void
  setIsRenamingBlockId: (blockId: string | null) => void
  setJustCreatedTableRowId: (rowId: string | null) => void
  setAiGeneration: (aiGeneration: EditorState['aiGeneration']) => void
}

export const createEditorStore = (initProps?: Partial<EditorState>) => {
  return createStore<EditorState>()((set) => ({
    selectedBlockId: null,
    hoveredBlockId: null,
    isRenamingBlockId: null,
    justCreatedTableRowId: null,
    selectedBlockAwareness: null,
    aiGeneration: null,
    recentlyUploadedImages: new Map(),
    setRecentlyUploadedImageById: (blockId, image) =>
      set((state) => ({
        recentlyUploadedImages: new Map(state.recentlyUploadedImages).set(
          blockId,
          image
        ),
      })),
    setSelectedBlockId: (blockId) =>
      set((state) => {
        if (state.selectedBlockId === blockId) return state
        return {
          selectedBlockId: blockId,
          selectedBlockAwareness: null,
        }
      }),
    setSelectedBlockAwareness: (awareness) =>
      set({ selectedBlockAwareness: awareness }),
    setHoveredBlockId: (blockId) => set({ hoveredBlockId: blockId }),
    setIsRenamingBlockId: (blockId) => set({ isRenamingBlockId: blockId }),
    setJustCreatedTableRowId: (rowId) => set({ justCreatedTableRowId: rowId }),
    setAiGeneration: (aiGeneration) => set({ aiGeneration }),
    ...initProps,
  }))
}

export type EditorStore = ReturnType<typeof createEditorStore>

const EditorStoreContext = React.createContext<EditorStore | null>(null)

export function useEditorStore<T>(selector: (state: EditorState) => T): T {
  const store = React.useContext(EditorStoreContext)
  if (!store) throw new Error('Missing EditorStoreContext.Provider in the tree')
  return useStore(store, selector)
}

export const EditorStoreProvider = ({
  children,
  prebuiltStore,
}: {
  children: React.ReactNode
  prebuiltStore?: EditorStore
}) => {
  const store = React.useMemo(
    () => prebuiltStore ?? createEditorStore(),
    [prebuiltStore]
  )

  const repoStoreRoot = useRepoStoreRoot()

  React.useEffect(() => {
    const unsub = store.subscribe((state) => {
      repoStoreRoot.setState({
        editorSelectedBlockId: state.selectedBlockId,
      })
    })

    return () => unsub()
  }, [repoStoreRoot, store])

  return (
    <EditorStoreContext.Provider value={store}>
      {children}
    </EditorStoreContext.Provider>
  )
}
